import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  AlertTriangle,
  CheckCircle2,
  Loader2,
  Play,
  XCircle,
} from 'lucide-react';

import { useSession } from '@/lib/useSession';
import {
  hasFaireAdmin,
  hasFaireViewer,
  relativeTime,
  useFaireRuns,
  useFaireTrigger,
  type FaireRun,
  type FaireRunStatus,
} from '@/lib/useFaireRuns';
import { useShipmentCounts } from '@/lib/useShipments';

type Stat = {
  label: string;
  value: string;
  trend?: string;
  trendDirection?: 'up' | 'down' | 'flat';
  pending?: boolean;
};

function StatusIcon({ status }: { status: FaireRunStatus }) {
  if (status === 'complete') {
    return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />;
  }
  if (status === 'failed') {
    return <XCircle className="h-4 w-4 text-red-600 dark:text-red-400" />;
  }
  if (status === 'partial') {
    return <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />;
  }
  return <Loader2 className="h-4 w-4 animate-spin text-amp-olive dark:text-amp-olive-dark" />;
}

function activityText(run: FaireRun): string {
  const orders = run.orders_in_pipefile ?? 0;
  const skipped = run.orders_skipped ?? 0;
  const trigger = run.trigger_source === 'manual' ? 'Manual run' : 'Scheduled run';
  if (run.status === 'failed') {
    return `${trigger} failed: ${run.error_summary ?? 'see error log'}`;
  }
  if (run.status === 'partial') {
    return `${trigger} partial: ${orders} order${orders === 1 ? '' : 's'} in pipefile, ${run.orders_failed ?? 0} failed`;
  }
  if (run.status === 'running') {
    return `${trigger} in progress`;
  }
  const noun = orders === 1 ? 'order' : 'orders';
  const skipNote = skipped > 0 ? ` (${skipped} dedupe skip${skipped === 1 ? '' : 's'})` : '';
  return `${trigger} complete: ${orders} ${noun} in pipefile${skipNote}`;
}

export function FaireDashboard() {
  const { user } = useSession();
  const groups = user?.groups ?? [];
  const isViewer = hasFaireViewer(groups, user?.superAdmin);
  const isAdmin = hasFaireAdmin(groups, user?.superAdmin);

  const runs = useFaireRuns(20);
  const shipmentCounts = useShipmentCounts();
  const trigger = useFaireTrigger();
  const [toast, setToast] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // KPI tile values. "Orders awaiting processing" replaces the prior
  // "Orders today" tile, which summed pipefile emissions across all runs
  // today and double-counted backfills. The unprocessed metric reads
  // directly from faire_sage_order_state.status and accumulates across
  // weekends, matching what Inside Sales actually owes when they open the
  // tool on a Monday.
  const stats: Stat[] = useMemo(() => {
    const sc = shipmentCounts.counts;
    return [
      {
        label: 'Awaiting customer mapping',
        value: shipmentCounts.isLoading ? '...' : String(sc.awaiting_customer_mapping),
        trend: sc.awaiting_customer_mapping === 0
          ? 'all retailers matched'
          : 'see Errors tab to map',
        trendDirection: 'flat',
      },
      {
        label: 'Ready to process',
        value: shipmentCounts.isLoading ? '...' : String(sc.ready_to_process),
        trend: 'matched, queued for Sage',
        trendDirection: 'flat',
      },
      {
        label: 'Processed today',
        value: shipmentCounts.isLoading ? '...' : String(sc.processed_today),
        trend: `${sc.total_unprocessed} still unprocessed`,
        trendDirection: 'flat',
      },
      {
        label: 'Shipments pending push',
        value: shipmentCounts.isLoading ? '...' : String(sc.eligible),
        trend: shipmentCounts.error
          ? 'API error'
          : `${sc.pushed_today} pushed today`,
        trendDirection: 'flat',
      },
    ];
  }, [
    shipmentCounts.counts,
    shipmentCounts.isLoading,
    shipmentCounts.error,
  ]);

  const recent = runs.runs.slice(0, 10);

  if (!isViewer) {
    return <AccessDeniedCard />;
  }

  const onRunNow = () => {
    setToast(null);
    trigger.mutate(undefined, {
      onSuccess: () => {
        setToast({ kind: 'ok', text: 'Poller started. Refresh in a few seconds for the new run.' });
      },
      onError: (err) => {
        setToast({ kind: 'err', text: `Trigger failed: ${err.message}` });
      },
    });
  };

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Dashboard</h1>
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Order flow into Sage. Auto-refresh every 15 seconds.
          </p>
        </div>
      </div>

      {toast && (
        <div
          className={`mb-4 rounded-md border px-3 py-2 text-sm ${
            toast.kind === 'ok'
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/40 dark:text-green-200'
              : 'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-900/40 dark:text-red-200'
          }`}
          role="status"
        >
          {toast.text}
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats.map((s) => (
          <div
            key={s.label}
            className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface"
          >
            <div className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">{s.label}</div>
            <div
              className={`text-2xl font-semibold mt-1 ${
                s.pending
                  ? 'text-amp-ink-muted dark:text-amp-dark-ink-muted'
                  : 'text-amp-ink dark:text-amp-dark-ink'
              }`}
            >
              {s.value}
            </div>
            {s.trend && (
              <div
                className={`text-xs mt-1 ${
                  s.trendDirection === 'up'
                    ? 'text-green-700 dark:text-green-300'
                    : s.trendDirection === 'down'
                      ? 'text-red-700 dark:text-red-300'
                      : 'text-amp-ink-muted dark:text-amp-dark-ink-muted'
                }`}
              >
                {s.trend}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-amp-line rounded-lg bg-amp-white dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <div className="border-b border-amp-line dark:border-amp-dark-line px-4 py-3 flex items-center justify-between">
            <h2 className="font-medium text-amp-ink dark:text-amp-dark-ink">Recent activity</h2>
            <Link
              to="/sales/faire/reports"
              className="text-xs text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink inline-flex items-center gap-1"
            >
              View all runs <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          {runs.isLoading ? (
            <div className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading runs...</div>
          ) : runs.error ? (
            <div className="px-4 py-6 text-sm text-red-700 dark:text-red-300">
              Could not load runs: {runs.error.message}
            </div>
          ) : recent.length === 0 ? (
            <div className="px-4 py-6 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
              No runs yet. Trigger one with Run now (admin only).
            </div>
          ) : (
            <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
              {recent.map((run) => (
                <li key={run.run_id} className="px-4 py-3 flex items-start gap-3">
                  <StatusIcon status={run.status} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-amp-ink dark:text-amp-dark-ink">{activityText(run)}</p>
                    <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      {relativeTime(run.started_at)} <span className="font-mono">{run.run_id.slice(0, 8)}</span>
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="border border-amp-line rounded-lg bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <h2 className="font-medium text-amp-ink dark:text-amp-dark-ink mb-3">Quick actions</h2>
          <ul className="space-y-2">
            {isAdmin && (
              <li>
                <button
                  type="button"
                  onClick={onRunNow}
                  disabled={trigger.isPending}
                  className="w-full text-left text-sm border border-amp-line text-amp-ink rounded px-3 py-2 hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm inline-flex items-center gap-2"
                >
                  {trigger.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="h-4 w-4" />
                  )}
                  {trigger.isPending ? 'Triggering...' : 'Run now'}
                </button>
              </li>
            )}
            <li>
              <Link
                to="/sales/faire/errors"
                className="block w-full text-left text-sm border border-amp-line text-amp-ink rounded px-3 py-2 hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
              >
                View errors
              </Link>
            </li>
            <li>
              <Link
                to="/sales/faire/shipments?status=eligible"
                className="block w-full text-left text-sm border border-amp-line text-amp-ink rounded px-3 py-2 hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
              >
                Push shipments to Faire
              </Link>
            </li>
            <li>
              <button
                type="button"
                disabled
                className="w-full text-left text-sm border border-amp-line text-amp-ink-muted rounded px-3 py-2 cursor-not-allowed opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink-muted"
              >
                Approve pending customers (Task 9)
              </button>
            </li>
            <li>
              <button
                type="button"
                disabled
                className="w-full text-left text-sm border border-amp-line text-amp-ink-muted rounded px-3 py-2 cursor-not-allowed opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink-muted"
              >
                Open Faire portal (Task 9)
              </button>
            </li>
          </ul>
          {!isAdmin && (
            <p className="text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted mt-3">
              Run now is admin-only. Contact IT for the cockpit-faire-sage-admin role.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function AccessDeniedCard() {
  return (
    <div>
      <div className="mb-6">
        <p className="text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark mb-1">Faire</p>
        <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Dashboard</h1>
      </div>
      <div className="border border-amp-line rounded-lg bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
        <h2 className="font-medium text-amp-ink dark:text-amp-dark-ink mb-2">You don't have access to Faire-Sage.</h2>
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Contact IT to request the cockpit-faire-sage-viewer role.
        </p>
      </div>
    </div>
  );
}
